import { ConfigApi, DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import {
  AddSelectionRequest,
  AwsAccountExclusions,
  ImcSyncApi,
  MetricProfile,
  SyncSelection,
} from './ImcSyncApi';

export class ImcSyncClient implements ImcSyncApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;
  private readonly configApi: ConfigApi;

  constructor(options: {
    discoveryApi: DiscoveryApi;
    fetchApi: FetchApi;
    configApi: ConfigApi;
  }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
    this.configApi = options.configApi;
  }

  private async getBaseUrl(): Promise<string> {
    return await this.discoveryApi.getBaseUrl('imc-sync');
  }

  async getProfiles(): Promise<MetricProfile[]> {
    // The catalog lives at IMC's public JSON URL (mirrors
    // scorecards.jsonDataUrl). Unconfigured → no profiles, so the picker
    // degrades to disabled rather than erroring the whole page.
    const url = this.configApi.getOptionalString('imcSync.profilesUrl');
    if (!url) {
      return [];
    }

    const response = await this.fetchApi.fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch metric profiles: ${response.statusText}`);
    }

    const body = await response.json();
    // Reject a malformed payload rather than rendering a broken dropdown; the
    // caller surfaces the error and the page still works for sync.
    if (
      !Array.isArray(body) ||
      !body.every(
        p =>
          p &&
          typeof p.key === 'string' &&
          typeof p.title === 'string' &&
          typeof p.description === 'string' &&
          Array.isArray(p.applicableMetricKeys) &&
          p.applicableMetricKeys.every((k: unknown) => typeof k === 'string'),
      )
    ) {
      throw new Error('Malformed metric-profiles response');
    }
    return body as MetricProfile[];
  }

  async getSelections(kind?: string): Promise<SyncSelection[]> {
    const baseUrl = await this.getBaseUrl();
    const query = kind ? `?kind=${encodeURIComponent(kind)}` : '';
    const response = await this.fetchApi.fetch(`${baseUrl}/selections${query}`);

    if (!response.ok) {
      throw new Error(`Failed to fetch selections: ${response.statusText}`);
    }

    return response.json();
  }

  async addSelection(request: AddSelectionRequest): Promise<SyncSelection> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/selections`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      throw new Error(`Failed to add selection: ${response.statusText}`);
    }

    return response.json();
  }

  async removeSelection(entityRef: string): Promise<void> {
    const baseUrl = await this.getBaseUrl();
    const encodedRef = encodeURIComponent(entityRef);
    const response = await this.fetchApi.fetch(
      `${baseUrl}/selections/${encodedRef}`,
      { method: 'DELETE' },
    );

    if (!response.ok) {
      throw new Error(`Failed to remove selection: ${response.statusText}`);
    }
  }

  async getAwsAccountExclusions(systemRef: string): Promise<string[]> {
    const baseUrl = await this.getBaseUrl();
    const query = `?system=${encodeURIComponent(systemRef)}`;
    const response = await this.fetchApi.fetch(
      `${baseUrl}/aws-account-exclusions${query}`,
    );

    if (!response.ok) {
      throw new Error(
        `Failed to fetch AWS account exclusions: ${response.statusText}`,
      );
    }

    const body = (await response.json()) as AwsAccountExclusions;
    // Reject a malformed or mismatched payload rather than silently treating it
    // as "no exclusions" — the caller then falls back to its safe load-failure
    // state instead of marking every account as included.
    if (
      !Array.isArray(body?.excludedAccountIds) ||
      !body.excludedAccountIds.every(id => typeof id === 'string') ||
      body.systemRef !== systemRef
    ) {
      throw new Error(
        `Malformed AWS account exclusions response for ${systemRef}`,
      );
    }
    return body.excludedAccountIds;
  }

  async setAwsAccountExclusions(
    systemRef: string,
    excludedAccountIds: string[],
  ): Promise<void> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/aws-account-exclusions`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ systemRef, excludedAccountIds }),
      },
    );

    if (!response.ok) {
      throw new Error(
        `Failed to save AWS account exclusions: ${response.statusText}`,
      );
    }
  }
}
